import React, {useMemo} from 'react'
import styled from 'styled-components'
import {getCommunityGuideline, getFontFamily, getFontSize} from 'utils'
import {useTranslation} from 'i18n'
import {useTheme} from 'themes'
import {LegalNavBarKey, parseShape} from 'types'
import {COMPANY_NAME} from 'consts'
import {Icon, Paragraph, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'

export interface LegalLikeAndCommentProps {
  scrollTo: (selectedSection: LegalNavBarKey | undefined) => void
  isSection: boolean
}

const StyledTextContainer = styled.div`
  text-align: justify;
`
const StyledSubTitle = styled(Paragraph)`
  ${({fontSize = 'xl', fontWeight = 'bold'}) => ({
    fontSize: typeof fontSize === 'number' ? fontSize : getFontSize(fontSize),
    fontFamily: getFontFamily(fontWeight),
  })}
  margin-top: ${convertUnit(20)};
  text-align: justify;
`
const StyledDescription = styled(Paragraph)`
  ${({fontSize = 'l'}) => ({
    fontSize: typeof fontSize === 'number' ? fontSize : getFontSize(fontSize),
  })}
  margin-top: ${convertUnit(10)};
  line-height: ${convertUnit(31)};
  white-space: pre-wrap;
  text-align: justify;
`

export default function LegalLikeAndComment({
  scrollTo,
  isSection,
}: LegalLikeAndCommentProps) {
  const theme = useTheme()
  const {translate} = useTranslation()

  const pattern = useMemo((): parseShape[] => {
    const regex = /\[(.+?)\]/
    return [
      {
        pattern: regex,
        style: {
          cursor: 'pointer',
          color: theme.primary_5,
          textDecoration: 'underline',
        },
        renderText: (text) => text.replace(regex, `$1`),
        onClick: () =>
          isSection
            ? window.open(getCommunityGuideline(), '_blank')
            : scrollTo('community-guidelines'),
      },
    ]
  }, [isSection, scrollTo, theme.primary_5])

  const handleTitle = useMemo(
    () => (
      <StyledTextContainer>
        <StyledSubTitle>
          {translate('policy:howItWorksLikeComment')}
        </StyledSubTitle>
        <StyledDescription>
          {translate('policy:howItWorksLikeCommentDesc')}
        </StyledDescription>
        <StyledSubTitle fontSize="l">
          {translate('policy:howItWorksLike')}
        </StyledSubTitle>
        <StyledDescription>
          {translate('policy:howItWorksLikeDesc_1')}
          <Icon type="like-outlined" />
          {translate('policy:howItWorksLikeDesc_2')}
          <Icon type="like-filled" />
          {translate('policy:howItWorksLikeDesc_3')}
        </StyledDescription>
        <StyledSubTitle fontSize="l">
          {translate('policy:howItWorksComment')}
        </StyledSubTitle>
        <StyledDescription>
          <ParsedText parse={pattern}>
            {translate('policy:howItWorksCommentDesc', {
              name: COMPANY_NAME,
            })}
          </ParsedText>
        </StyledDescription>
      </StyledTextContainer>
    ),
    [pattern, translate],
  )

  return <>{handleTitle}</>
}
